import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import { analyzeOccupancyTrends } from '../../../services/aiAnalytics';
import toast from 'react-hot-toast';

const VacancyPredictionAlerts = ({ predictions = [], stats = {} }) => {
  const navigate = useNavigate();
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (error) {
      toast?.error(error?.message || 'Failed to analyze occupancy trends');
    }
  }, [error]);

  useEffect(() => {
    if (stats?.occupiedUnits && stats?.totalUnits) {
      loadAIAnalysis();
    }
  }, [stats]);

  const loadAIAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const analysisData = {
        occupiedUnits: stats?.occupiedUnits || 0,
        totalUnits: stats?.totalUnits || 0,
        historicalData: predictions || [],
        expiringLeases: stats?.expiringLeases || 0
      };
      
      const result = await analyzeOccupancyTrends(analysisData);
      setAiAnalysis(result);
    } catch (err) {
      console.error('AI analysis error:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high': case'critical':
        return 'text-danger-600';
      case 'medium':
        return 'text-warning-600';
      default:
        return 'text-success-600';
    }
  };

  const getRiskBgColor = (risk) => {
    switch (risk) {
      case 'high': case'critical':
        return 'bg-danger-50 border-danger-200';
      case 'medium':
        return 'bg-warning-50 border-warning-200';
      default:
        return 'bg-success-50 border-success-200';
    }
  };

  const getRiskIcon = (risk) => {
    switch (risk) {
      case 'high': case'critical':
        return 'AlertTriangle';
      case 'medium':
        return 'AlertCircle';
      default:
        return 'CheckCircle';
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Icon name="TrendingDown" className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">AI Vacancy Prediction</h3>
            <p className="text-sm text-muted-foreground">Powered by OpenAI</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Icon name="TrendingDown" className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">AI Vacancy Prediction</h3>
            <p className="text-sm text-muted-foreground">Powered by OpenAI</p>
          </div>
        </div>
        <button
          onClick={loadAIAnalysis}
          disabled={loading}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
          title="Refresh analysis"
        >
          <Icon name="RefreshCw" className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {aiAnalysis ? (
        <>
          {/* Risk Level Badge */}
          <div className={`p-4 rounded-lg border mb-4 ${getRiskBgColor(aiAnalysis?.riskLevel)}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name={getRiskIcon(aiAnalysis?.riskLevel)} className={`w-5 h-5 ${getRiskColor(aiAnalysis?.riskLevel)}`} />
                <span className="text-sm font-medium text-foreground">
                  {aiAnalysis?.riskLevel?.toUpperCase()} RISK
                </span>
              </div>
              <div className="text-right">
                <div className={`text-2xl font-bold ${getRiskColor(aiAnalysis?.riskLevel)}`}>
                  {aiAnalysis?.predictedOccupancyRate?.toFixed(1)}%
                </div>
                <div className="text-xs text-muted-foreground">Predicted Rate</div>
              </div>
            </div>
          </div>

          {/* AI Insights */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
              <Icon name="Lightbulb" className="w-4 h-4" />
              AI Insights
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {aiAnalysis?.insights}
            </p>
          </div>

          {/* Recommendations */}
          {aiAnalysis?.recommendations?.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Icon name="CheckSquare" className="w-4 h-4" />
                Recommended Actions
              </h4>
              <ul className="space-y-2">
                {aiAnalysis?.recommendations?.map((rec, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-foreground">
                    <Icon name="ArrowRight" className="w-4 h-4 text-primary-600 mt-0.5 flex-shrink-0" />
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Button
            onClick={() => navigate('/vacancy-prediction')}
            variant="outline"
            className="w-full"
            iconName="ArrowRight"
            iconPosition="right"
          >
            View Detailed Analysis
          </Button>
        </>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="AlertCircle" className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No prediction data available</p>
        </div>
      )}
    </Card>
  );
};

export default VacancyPredictionAlerts;